import React from "react";
import "./TenantSwitchModal.css";

const tenantInitialFromName = (name) => {
  const s = String(name ?? "").trim();
  if (!s) return "?";
  return s.charAt(0).toUpperCase();
};

const IcBuilding = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2.5 14.5V5.2L8 2l5.5 3.2v9.3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.5 14.5v-5h5v5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IcSearch = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M7.2 12.6a5.4 5.4 0 1 1 0-10.8 5.4 5.4 0 0 1 0 10.8Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
    <path
      d="M11.2 11.2L14.6 14.6"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const TenantSwitchModal = ({
  show,
  activeTenantName,
  availableTenants = [],
  currentAccountId,
  switchingTenant = false,
  onSwitchTenant,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [switchingTargetId, setSwitchingTargetId] = React.useState(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (!show) return;
    setSearchQuery("");
    setSwitchingTargetId(null);
    window.setTimeout(() => inputRef.current?.focus?.(), 0);
  }, [show]);

  React.useEffect(() => {
    if (!switchingTenant) setSwitchingTargetId(null);
  }, [switchingTenant]);

  React.useEffect(() => {
    if (!show) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [show, onClose]);

  const filteredTenants = React.useMemo(() => {
    const q = String(searchQuery ?? "").trim().toLowerCase();
    const normalized = Array.isArray(availableTenants) ? availableTenants : [];
    if (!q) return normalized;
    return normalized.filter((t) => {
      const id = String(t?.accountID ?? t?.accountId ?? t?.id ?? "");
      const label = t?.accountName || t?.name || id || "Organization";
      const emailLike = t?.email || "";
      return (
        label.toLowerCase().includes(q) ||
        id.toLowerCase().includes(q) ||
        String(emailLike).toLowerCase().includes(q)
      );
    });
  }, [availableTenants, searchQuery]);

  const curId = currentAccountId != null ? String(currentAccountId) : null;

  const sortedTenants = React.useMemo(() => {
    if (!curId) return filteredTenants;
    const list = filteredTenants.slice();
    list.sort((a, b) => {
      const aid = String(a?.accountID ?? a?.accountId ?? a?.id ?? "");
      const bid = String(b?.accountID ?? b?.accountId ?? b?.id ?? "");
      const aIsCur = aid && String(aid) === curId;
      const bIsCur = bid && String(bid) === curId;
      if (aIsCur === bIsCur) return 0;
      return aIsCur ? -1 : 1;
    });
    return list;
  }, [filteredTenants, curId]);

  if (!show) return null;

  return (
    <div
      className="tsm-overlay"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        className="tsm-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Switch workspace"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tsm-header">
          <div className="tsm-header__left">
            <div className="tsm-header__icon" aria-hidden="true">
              <IcBuilding />
            </div>
            <div className="tsm-header__titles">
              <div className="tsm-header__title">Switch workspace</div>
              <div className="tsm-header__sub">
                Active: {activeTenantName || "—"}
              </div>
            </div>
          </div>
           <button
            type="button"
            className="tsm-close"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            disabled={switchingTenant}
          >
            X
          </button>
        </div>

        {/* Body */}
        <div className="tsm-body">
          {/* Search */}
          <div className="tsm-search">
            <span className="tsm-search__icon">
              <IcSearch />
            </span>
            <input
              ref={inputRef}
              className="tsm-search__input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name"
              disabled={switchingTenant}
              aria-label="Search workspaces"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                className="tsm-search__clear"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
                tabIndex={-1}
              >
                ×
              </button>
            )}
          </div>

          {/* Grid */}
          <div className="tsm-section-label">Select a workspace</div>

          {sortedTenants.length > 0 ? (
            <div className="tsm-grid-scroll">
              <div className="tsm-grid" role="listbox" aria-label="Workspaces">
                {sortedTenants.map((t, i) => {
                  const id = String(t?.accountID ?? t?.accountId ?? t?.id ?? "");
                  const label = t?.accountName || t?.name || id || "Organization";
                  const isCurrent = curId != null && curId === id;
                  const isLoading =
                    switchingTenant &&
                    switchingTargetId != null &&
                    String(switchingTargetId) === id;

                  return (
                    <button
                      key={id || label}
                      type="button"
                      className={`tsm-card${isCurrent ? " tsm-card--active" : ""}`}
                      role="option"
                      aria-selected={isCurrent}
                      disabled={switchingTenant || isCurrent}
                      style={{ animationDelay: `${i * 0.04}s` }}
                      onClick={() => {
                        if (switchingTenant || isCurrent) return;
                        setSwitchingTargetId(id);
                        if (typeof onSwitchTenant === "function") onSwitchTenant(t);
                      }}
                    >
                      {/* Loading overlay */}
                      {isLoading && (
                        <div className="tsm-card__loading" aria-hidden="true">
                          <span className="tsm-spinner" />
                        </div>
                      )}

                      {/* Avatar */}
                      <div className="tsm-card__avatar-wrap">
                        {t?.accountLogo ? (
                          <img
                            className="tsm-card__logo"
                            src={t.accountLogo}
                            alt=""
                          />
                        ) : (
                          <span className="tsm-card__initial" aria-hidden="true">
                            {tenantInitialFromName(label)}
                          </span>
                        )}
                      </div>

                      {/* Text */}
                      <div className="tsm-card__text">
                        <span className="tsm-card__name">{label}</span>
                      </div>

                      {/* Footer */}
                      <div className="tsm-card__footer">
                        {isCurrent ? (
                          <span className="tsm-badge">Current</span>
                        ) : (
                          <span className="tsm-cta">Switch here →</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="tsm-empty">
              <div className="tsm-empty__icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="7.5" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  <path d="M8.5 11h5M11 8.5v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </div>
              <p>
                No workspaces found
                {searchQuery ? (
                  <>
                    {" "}for <strong>"{searchQuery}"</strong>
                  </>
                ) : ""}
              </p>
            </div>
          )}

          {/* Switching status */}
          {switchingTenant && (
            <div className="tsm-status">
              <span className="tsm-status__dot" aria-hidden="true" />
              <span className="tsm-status__text">Updating session and workspace…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantSwitchModal;